import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { Modal } from '@bb/ui';
import type { AdminApi, SearchHit } from '../../api/client.ts';

const TYPE_LABEL: Record<string, string> = {
  app: 'App',
  internal_group: 'Internal group',
  attribute_definition: 'Attribute',
  event_definition: 'Event',
  canvas: 'Canvas',
  campaign: 'Campaign',
  segment: 'Segment',
  template: 'Template',
  content_block: 'Content block',
  media_asset: 'Media',
};

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Global search of the environment (BB-18): `⌘K` / `Ctrl+K` or the header button opens it; arrows move through the
 * results and Enter opens the object. Results come from the server, which only returns what the employee may view.
 */
export function CommandPalette({ environmentId }: { environmentId: string }) {
  const router = useRouter();
  const { api } = router.options.context as { api: AdminApi };
  const id = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), 150);
    return () => clearTimeout(timer);
  }, [text]);

  const results = useQuery({ queryKey: ['search', environmentId, query], queryFn: () => api.search(environmentId, query), enabled: open && query.length > 0 });
  const hits = query ? (results.data ?? []) : [];
  const close = () => {
    setOpen(false);
    setText('');
    setQuery('');
    setActive(0);
  };
  const go = (hit: SearchHit) => {
    close();
    router.history.push(hit.url);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, hits.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && hits[active]) {
      event.preventDefault();
      go(hits[active]);
    }
  };
  const listId = `${id}-results`;
  const status = !query ? 'Type a name, an API id or a tag.' : results.isPending ? 'Searching…' : results.isError ? 'Search failed.' : hits.length === 0 ? 'Nothing found.' : `${hits.length} results`;

  return (
    <>
      <button type="button" className="button shell__search" onClick={() => setOpen(true)} aria-keyshortcuts="Meta+K Control+K">
        Search <kbd>{isMac() ? '⌘K' : 'Ctrl+K'}</kbd>
      </button>
      <Modal open={open} onOpenChange={(value) => (value ? setOpen(true) : close())} title="Search">
        <div className="palette">
          <input
            className="bb-input"
            role="combobox"
            aria-label="Search this environment"
            aria-expanded={hits.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={hits[active] ? `${id}-hit-${active}` : undefined}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <p className="hint" role="status">
            {status}
          </p>
          <ul id={listId} role="listbox" aria-label="Results" className="palette__results">
            {hits.map((hit, index) => (
              <li
                key={`${hit.objectType}:${hit.objectId}`}
                id={`${id}-hit-${index}`}
                role="option"
                aria-selected={index === active}
                className={index === active ? 'palette__hit palette__hit--active' : 'palette__hit'}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  go(hit);
                }}
              >
                <span className="tag tag--neutral">{TYPE_LABEL[hit.objectType] ?? hit.objectType}</span> <strong>{hit.title}</strong>
                {hit.subtitle ? <span className="hint"> · {hit.subtitle}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </>
  );
}
